import React from "react";
import { Link } from "react-router-dom";
import { Check, Shield } from "lucide-react";
import { TARIFS, INSCRIPTIONS_URL } from "@/lib/data";
import { getFormulas } from "@/lib/formulas";

export default function Tarifs() {
  const { insurance } = TARIFS;
  const [formulas, setFormulas] = React.useState(TARIFS.formulas);
  React.useEffect(() => { getFormulas().then(setFormulas).catch(() => {}); }, []);
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
      <p className="he-overline text-neutral-500">Saison 2026-2027</p>
      <h1 className="font-heading text-5xl sm:text-6xl font-black tracking-tighter mt-3">Nos formules</h1>
      <p className="mt-4 text-neutral-600 font-body max-w-2xl">{TARIFS.subtitle}</p>
      <a
        href="/docs/tarifs-2026-2027.pdf"
        target="_blank"
        rel="noreferrer"
        data-testid="tarifs-pdf-download"
        className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[var(--he-red)] link-underline"
      >
        Télécharger la fiche tarifs & infos pratiques (PDF) ↓
      </a>

      {/* Formules */}
      <section className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
        {formulas.map((f) => (
          <article
            key={f.slug}
            data-testid={`tarif-${f.slug}`}
            className={`relative flex flex-col rounded-2xl border p-6 transition-colors ${
              f.highlight
                ? "border-[var(--he-red)] bg-black text-white he-grain overflow-hidden"
                : "border-black/10 hover:border-[var(--he-red)]"
            }`}
          >
            {f.highlight && (
              <span className="he-overline text-[var(--he-red)] mb-1">La plus libre</span>
            )}
            <h3 className="font-heading text-2xl font-black tracking-tighter uppercase">{f.name}</h3>

            <div className="mt-5">
              <span data-testid={`tarif-price-${f.slug}`} className={`font-heading text-5xl font-black tracking-tighter ${f.highlight ? "" : "text-black"}`}>
                {f.price}
              </span>
              <div className={`text-xs mt-1 ${f.highlight ? "text-neutral-400" : "text-neutral-500"}`}>{f.period}</div>
            </div>

            <ul className={`mt-6 space-y-2 text-sm font-body ${f.highlight ? "text-white/85" : "text-neutral-600"}`}>
              {f.features.map((ft) => (
                <li key={ft} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[var(--he-red)] mt-0.5 shrink-0" /> {ft}
                </li>
              ))}
            </ul>

            {/* Bloc assurance distinct */}
            {f.annual ? (
              <div
                data-testid={`tarif-insurance-${f.slug}`}
                className={`mt-6 rounded-xl border border-dashed p-4 ${
                  f.highlight ? "border-white/25 bg-white/5" : "border-black/15 bg-neutral-50"
                }`}
              >
                <div className="flex items-center gap-2 text-xs font-bold">
                  <Shield className="w-4 h-4 text-[var(--he-red)]" />
                  <span>Protéger mon abonnement</span>
                </div>
                <div className={`mt-2 text-xs ${f.highlight ? "text-neutral-300" : "text-neutral-500"}`}>
                  Assurtacotiz en option : <strong>+ {f.insurance.price}</strong> / an
                </div>
                <div className={`mt-1 text-xs ${f.highlight ? "text-neutral-300" : "text-neutral-500"}`}>
                  Avec assurance : <strong>{f.total.price} / an</strong>
                </div>
              </div>
            ) : (
              <div className="mt-6 rounded-xl border border-dashed border-black/15 bg-neutral-50 p-4 text-xs text-neutral-500 flex items-start gap-2">
                <Shield className="w-4 h-4 text-neutral-400 mt-0.5" />
                Assurtacotiz non proposée : la formule Découverte n’est pas un abonnement annuel.
              </div>
            )}

            <div className="mt-6 pt-2 flex flex-col gap-2 mt-auto">
              {f.payment_url ? (
                <a
                  href={f.payment_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`tarif-cta-${f.slug}`}
                  className="btn-red rounded-full px-5 py-2.5 text-sm text-center"
                >
                  {f.cta} →
                </a>
              ) : (
                <a
                  href={INSCRIPTIONS_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`tarif-cta-${f.slug}`}
                  className="btn-red rounded-full px-5 py-2.5 text-sm text-center"
                >
                  {f.cta} →
                </a>
              )}
              {f.annual && (
                <a href="#assurtacotiz" className="text-xs text-center text-neutral-500 hover:text-[var(--he-red)] transition-colors">
                  En savoir plus sur l’assurance
                </a>
              )}
            </div>
          </article>
        ))}
      </section>

      <p className="mt-6 text-xs text-neutral-500">{TARIFS.membershipNote}</p>

      {/* Calcul assurance */}
      <section id="assurtacotiz" className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <p className="he-overline text-neutral-500">En option, si vous le souhaitez</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Protégez votre abonnement</h2>
          <p className="mt-4 text-neutral-600 font-body">{insurance.intro}</p>
          <div className="mt-6 p-5 border border-black/10 rounded-2xl bg-neutral-50">
            <div className="he-overline text-neutral-500">Comment est calculée l’assurance ?</div>
            <p className="mt-2 text-sm font-body">
              Le tarif correspond à <strong>5 % du montant de l’abonnement annuel</strong>, avec un minimum de <strong>20 € par an</strong>.
              Le minimum annuel de 20 € explique que l’assurance des formules Essentiel et Passion soit facturée 20 €.
            </p>
            <div className="mt-4 overflow-x-auto">
              <table data-testid="assurtacotiz-table" className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-black/10">
                    <th className="py-2 pr-4">Formule</th>
                    <th className="py-2 pr-4">Abonnement</th>
                    <th className="py-2 pr-4">Assurance facultative</th>
                    <th className="py-2">Total avec assurance</th>
                  </tr>
                </thead>
                <tbody className="font-body">
                  {formulas.filter(f => f.annual).map((f) => (
                    <tr key={f.slug} className="border-b border-black/5">
                      <td className="py-2.5 pr-4 font-bold">{f.name}</td>
                      <td className="py-2.5 pr-4">{f.price}</td>
                      <td className="py-2.5 pr-4 text-[var(--he-red)]">+ {f.insurance.price}</td>
                      <td className="py-2.5 font-bold">{f.total.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm font-body">
              <Shield className="w-4 h-4 inline text-[var(--he-red)] mr-1" />
              <strong>Accessible quel que soit l’âge</strong> — {insurance.age}
            </p>
          </div>
        </div>

        {/* À quoi sert Assurtacotiz */}
        <div className="bg-black text-white rounded-2xl p-8 he-grain">
          <p className="he-overline text-neutral-400">Assurtacotiz</p>
          <h3 className="font-heading text-3xl font-bold tracking-tight mt-2">À quoi sert Assurtacotiz ?</h3>
          <p className="mt-4 text-sm text-white/80 font-body">
            Assurtacotiz permet, sous réserve des conditions du contrat, de bénéficier d’une prise en charge de tout ou partie
            de l’abonnement annuel lorsque certains événements empêchent durablement la pratique de l’activité.
          </p>
          <ul className="mt-5 space-y-2 text-sm font-body text-white/85">
            {insurance.covers.map((c) => (
              <li key={c} className="flex items-start gap-2"><Check className="w-4 h-4 text-[var(--he-red)] mt-0.5 shrink-0" /> {c}</li>
            ))}
          </ul>
          <p className="mt-6 text-xs text-neutral-400 font-body border-t border-white/10 pt-4">{insurance.disclaimer}</p>
          <a
            data-testid="assurtacotiz-doc"
            href={insurance.docUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-6 inline-block rounded-full px-5 py-2.5 text-sm border border-white/40 hover:bg-white hover:text-black transition-colors"
          >
            Découvrir les garanties (conditions générales PDF)
          </a>
        </div>
      </section>

      {/* Infos pratiques & paiement */}
      <section className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
        <InfoCard
          title="Moyens de paiement"
          items={["Espèces", "Virement bancaire", "Carte bleue", "Chèques (3 maximum — premier encaissement en septembre)"]}
        />
        <InfoCard
          title="Réductions"
          items={[
            "* Tarifs réduits : mineurs, étudiants, demandeurs d'emploi, personnes en situation de handicap — sur justificatif à fournir sous 48h (sinon remboursement et place perdue)",
            "Tarif partenaire MJC : 229,50 €/an pour le 2e cours annuel souscrit chez Afrolat'Studio (offre non cumulable)",
          ]}
        />
        <InfoCard
          title="Bon à savoir"
          items={[
            "30 séances par an — pas de cours pendant les vacances scolaires",
            "Dossier d'inscription complet obligatoire avant paiement",
            "Place non conservée en cas d'absence prolongée",
            "Pas de remboursement",
          ]}
        />
      </section>

      {/* Help block */}
      <section className="mt-16 p-8 border border-black/10 rounded-2xl bg-neutral-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="font-heading text-xl font-bold">Besoin d’aide pour choisir votre formule ?</h3>
          <p className="text-sm text-neutral-600 font-body mt-1">Nous vous répondons rapidement pour vous orienter.</p>
        </div>
        <Link to="/contact" className="btn-red rounded-full px-6 py-3 text-sm">Nous contacter</Link>
      </section>
    </div>
  );
}

function InfoCard({ title, items }) {
  return (
    <div className="p-6 border border-black/10 rounded-2xl">
      <h3 className="font-heading text-lg font-bold">{title}</h3>
      <ul className="mt-4 space-y-2 text-sm text-neutral-600 font-body">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2">
            <Check className="w-4 h-4 text-[var(--he-red)] mt-0.5 shrink-0" /> {it}
          </li>
        ))}
      </ul>
    </div>
  );
}
